import ISmartScheduleDataService from "./ISmartScheduleDataService";
import RestClient from "sh-rest-client";
import { DataService } from "./DataService";
import { IGenerateScheduleRequest, IGenerateScheduleWithHistoryRequest } from "sh-rest-client/GenerateScheduleRequest";
import { AxiosResponse } from "axios";
import { TeamStore } from "sh-team-store";
import MemberUtils from "sh-application/utility/MemberUtils";
import { updateSmartSchedulePollingStatus } from "sh-application/components/smartSchedule/mutatorActions/pollingActions";
import { JobStatus } from "sh-models/job/SmartScheduleGenerationJobStatusEntity";

/**
 * Data service encapsulating Smart Schedule generation operations.
 * Follows existing pattern of thin wrappers around RestClient functionality.
 */
class SmartScheduleDataService extends DataService implements ISmartScheduleDataService {

    /**
     * Generates a schedule for a tag (scheduling group) within the current tenant/team context.
     * @param teamId Team identifier.
     * @param tagId Tag / scheduling group identifier.
     * @param request Request body defining the date range and shift templates.
     */
    public generateScheduleForTag(teamId: string, tagId: string, request: IGenerateScheduleRequest): Promise<AxiosResponse> {
        // tenantId comes from logged in context (DataService.setUserLoggedIn) but fall back to TeamStore for safety
        const tenantId = this.tenantId || TeamStore()?.tenantId;
        return RestClient
            .generateScheduleForTag(tenantId, teamId, tagId, request)
            .then((response: AxiosResponse) => {
                // Extract jobId from service response (assumes response.data contains job/jobId)
                const responseJobId: string | undefined = (response as any)?.data?.jobId;
                const jobIdToUse = responseJobId || teamId; // fallback for safety if API shape changes
                const currentMemberId = MemberUtils.getCurrentUserMemberId() || "";
                updateSmartSchedulePollingStatus({
                    jobId: jobIdToUse,
                    tagId,
                    teamId,
                    memberId: currentMemberId,
                    status: JobStatus.InProgress,
                    result: {
                        teamId,
                        tagId,
                        jobId: jobIdToUse,
                        memberId: currentMemberId,
                        createdShiftIds: null,
                        createdOpenShiftIds: null
                    },
                    jobProperties: {
                        overrideExistingSchedule: request.overrideExistingSchedule,
                        isReviewed: false,
                        startDate: request.startDate,
                        endDate: request.endDate
                    }
                });
                return response;
            })
            .catch((e: any) => {
                const currentMemberId = MemberUtils.getCurrentUserMemberId() || "";
                updateSmartSchedulePollingStatus({
                    jobId: teamId, // failure before job id known: retain teamId placeholder
                    tagId,
                    teamId,
                    memberId: currentMemberId,
                    status: JobStatus.Failure,
                    result: {
                        teamId,
                        tagId,
                        jobId: teamId,
                        memberId: currentMemberId,
                        createdShiftIds: null,
                        createdOpenShiftIds: null,
                        error: {
                            code: "AutoScheduleGenerationFailed",
                            message: e?.message || "generation request failed"
                        }
                    },
                    jobProperties: {
                        overrideExistingSchedule: request.overrideExistingSchedule,
                        isReviewed: false,
                        startDate: request.startDate,
                        endDate: request.endDate
                    }
                });
                throw e; // propagate error for caller handling
            });
    }

    /**
     * Triggers auto-assignment of open shifts for a tag using scheduling history.
     */
    public generateScheduleWithHistory(teamId: string, tagId: string, request: IGenerateScheduleWithHistoryRequest): Promise<AxiosResponse> {
        const tenantId = this.tenantId || TeamStore()?.tenantId;
        return RestClient.generateScheduleWithHistory(tenantId, teamId, tagId, request);
    }

    /**
     * Resets sync state (no-op for SmartSchedule at present).
     */
    public async resetSyncState(): Promise<void> { /* no persistent smart schedule state to reset */ }
}

const service: ISmartScheduleDataService = new SmartScheduleDataService();
export default service;
